import React, { useEffect, useRef, useState } from 'react'
import { Check, Star, Trophy, Sparkles } from 'lucide-react'
import type { Card } from '../types/LessonDoc'
import LessonCardRenderer from './LessonCardRenderer'
import { LessonStatsService } from '../../../services/stats/LessonStatsService'
import { gamificationApi } from '../api/gamificationApi'
import { celebrateCorrect, celebrateBig } from '../../../components/interactive/shared/effects/feedbackEffects'
import { soundService } from '../../../services/audio/SoundEffectService'
import QuestClearModal, { type QuestClearData } from './QuestClearModal'
import SectionClearModal, { type SectionClearData } from './SectionClearModal'

// Câu hỏi "chốt" trong một thẻ rich — quyết định thẻ có phải "ải có câu hỏi" hay không.
const GATE_TYPES = new Set(['interactive_question', 'multiple_response_question', 'sequence_question'])
function isInteractiveVideoCard(card: any): boolean {
  if (!card || !Array.isArray(card.checkpoints) || card.checkpoints.length === 0) return false
  if (card.type === 'video') return !!(card.youtubeId || card.src)
  if (card.type === 'pptx') return !!card.recordingUrl
  return false
}

function gateCount(card: Card): number {
  if (isInteractiveVideoCard(card)) return 1
  if ((card as any).type !== 'rich') return 0
  const items = (card as any).items || []
  return items.filter((it: any) => it && GATE_TYPES.has(it.type)).length
}

const XP_PER_QUEST = 10
const XP_PER_SECTION = 20

interface QuestSectionProps {
  cards: Card[]
  statsKey: string        // key LessonStatsService của bài (vd 'bai-giang:<slug>')
  sectionIndex: number    // chỉ số phần đang xem (bucket thống kê dùng chỉ số này)
  sectionTitle: string
  lessonSlug: string
  accent: string          // màu điểm nhấn của phần
  bypassLock: boolean      // GV/Admin: mở hết, không tính thưởng
}

// Lộ trình "vượt ải" cho phần Hình thành kiến thức: mỗi thẻ/Hoạt động = 1 ải.
// Lộ dần từng ải; trả lời hết câu hỏi chốt trong ải → nhận sao + hiệu ứng + XP, mở ải kế.
const QuestSection: React.FC<QuestSectionProps> = ({
  cards, statsKey, sectionIndex, sectionTitle, lessonSlug, accent, bypassLock,
}) => {
  const gates = cards.map(gateCount)

  // KHÔI PHỤC ải ĐÃ QUA từ tiến độ đã lưu (answeredKeys đã hydrate từ backend vào LessonStatsService).
  // Ải done khi: có khoá '<cardId>:__done__' (ải không câu hỏi đã bấm qua) HOẶC đã trả lời đủ câu hỏi chốt.
  const computeRestored = (): { doneSet: Set<number>; unlockedTo: number } => {
    const doneSet = new Set<number>()
    if (bypassLock) return { doneSet, unlockedTo: 0 }
    const ak = LessonStatsService.getAnsweredKeys(statsKey)
    cards.forEach((c, i) => {
      if (ak.has(`${c.id}:__done__`)) { doneSet.add(i); return }
      if (gates[i] > 0) {
        let cnt = 0
        ak.forEach((k) => { if (k.startsWith(`${c.id}:`) && !k.endsWith(':__done__')) cnt++ })
        if (cnt >= gates[i]) doneSet.add(i)
      }
    })
    let maxDone = -1
    doneSet.forEach((i) => { if (i > maxDone) maxDone = i })
    return { doneSet, unlockedTo: Math.max(0, maxDone + 1) }
  }
  const restoredRef = useRef<{ doneSet: Set<number>; unlockedTo: number } | null>(null)
  if (restoredRef.current === null) restoredRef.current = computeRestored()

  const [unlocked, setUnlocked] = useState(restoredRef.current.unlockedTo)  // chỉ số ải xa nhất đã mở
  const [done, setDone] = useState<Set<number>>(restoredRef.current.doneSet)
  const [stars, setStars] = useState<Record<number, number>>(() => {
    const m: Record<number, number> = {}
    restoredRef.current!.doneSet.forEach((i) => { m[i] = 3 })   // ải khôi phục: hiện ✓ (không tính lại sao)
    return m
  })
  const [clear, setClear] = useState<QuestClearData | null>(null)        // modal qua ải
  const [sectionClear, setSectionClear] = useState<SectionClearData | null>(null)  // modal hạ trùm
  const [fx, setFx] = useState<boolean>(() => {
    try { return localStorage.getItem('questFxEnabled') !== 'false' } catch { return true }
  })
  const wrongAtStart = useRef<Record<number, number>>({})
  const prevLevel = useRef<number>(0)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  const toggleFx = () => setFx((v) => {
    const nv = !v
    try { localStorage.setItem('questFxEnabled', nv ? 'true' : 'false'); soundService.setEnabled(nv) } catch { /* noop */ }
    return nv
  })

  // Seed level hiện tại của HS (để phát hiện "lên cấp" đúng ngay từ ải đầu tiên).
  useEffect(() => {
    if (bypassLock) return
    gamificationApi.me().then((s) => { if (s) prevLevel.current = s.level }).catch(() => { /* noop */ })
  }, [bypassLock])

  // Snapshot số câu SAI của bucket khi một ải trở thành ải hiện tại (để tính sao theo từng ải).
  useEffect(() => {
    if (bypassLock) return
    const i = unlocked
    if (i < cards.length && wrongAtStart.current[i] === undefined) {
      const b = LessonStatsService.getStats(statsKey)?.sections?.[sectionIndex]
      wrongAtStart.current[i] = b?.wrong || 0
    }
  }, [unlocked, bypassLock, cards.length, statsKey, sectionIndex])

  const complete = (i: number) => {
    if (bypassLock || done.has(i)) return
    // Đánh dấu ải đã qua để khôi phục khi load lại (đặc biệt ải KHÔNG có câu hỏi — bấm "Đã hiểu").
    try { LessonStatsService.recordAnswered(`${cards[i].id}:__done__`, statsKey) } catch { /* noop */ }
    if (isInteractiveVideoCard(cards[i])) {
      try { LessonStatsService.recordVideoWatched(statsKey) } catch { /* noop */ }
    }
    const hasQ = gates[i] > 0
    let st = 0
    if (hasQ) {
      const b = LessonStatsService.getStats(statsKey)?.sections?.[sectionIndex]
      const base = wrongAtStart.current[i] ?? (b?.wrong || 0)
      const delta = Math.max(0, (b?.wrong || 0) - base)
      st = delta <= 0 ? 3 : delta === 1 ? 2 : 1
    }
    setStars((s) => ({ ...s, [i]: st }))
    setDone((d) => new Set(d).add(i))
    if (fx) {
      celebrateCorrect(cardRefs.current[i])
      try { soundService.playStars(hasQ ? st : 1) } catch { /* noop */ }
    }

    const next = i + 1
    const isLast = next >= cards.length
    const cardTitle = cards[i].title || `Hoạt động ${i + 1}`

    // Mở modal "qua ải" NGAY (level/xp cập nhật sau khi server trả).
    setClear({ cardIndex: i, cardTitle, hasQ, stars: st, xpGain: XP_PER_QUEST, isLast, accent })
    setUnlocked((u) => Math.max(u, next))

    // Cấp phần thưởng + lấy level/xp để chạy thanh tiến độ trong modal.
    gamificationApi.grant({
      key: `quest:${lessonSlug}:${sectionIndex}:${i}`,
      type: 'lesson_quest',
      title: `Qua ải: ${cardTitle}`,
      icon: '⭐',
      xp: XP_PER_QUEST,
      stars: st,
      meta: { lesson: lessonSlug, section: sectionIndex, card: i },
    }).then((res) => {
      if (!res) return
      const leveledUp = prevLevel.current > 0 && res.level > prevLevel.current
      prevLevel.current = res.level
      setClear((c) => (c && c.cardIndex === i
        ? { ...c, level: res.level, xpInLevel: res.xpInLevel, xpForNextLevel: res.xpForNextLevel, leveledUp }
        : c))
      if (leveledUp && fx) { try { soundService.playLevelUp() } catch { /* noop */ } }
    })

    if (isLast) {
      gamificationApi.grant({
        key: `badge:${lessonSlug}:${sectionIndex}`,
        type: 'badge',
        title: `Chinh phục: ${sectionTitle}`,
        description: 'Hoàn thành toàn bộ các ải kiến thức của phần này.',
        icon: '🏅',
        xp: XP_PER_SECTION,
        meta: { lesson: lessonSlug, section: sectionIndex, badge: true },
      })
    }
  }

  // Nút "Tiếp tục" trong modal qua ải: ải cuối → mở màn hạ trùm; ngược lại cuộn tới ải sau.
  const onQuestContinue = () => {
    const cur = clear
    setClear(null)
    if (!cur) return
    if (cur.isLast) {
      const ids = Array.from({ length: cards.length }, (_, k) => k)
      const totalStars = ids.reduce((sum, k) => sum + (stars[k] ?? 0), 0)
      const maxStars = gates.filter((g) => g > 0).length * 3
      if (fx) { celebrateBig(); try { soundService.playFanfare() } catch { /* noop */ } }
      setSectionClear({
        sectionTitle, questCount: cards.length, totalStars, maxStars,
        xpGain: cards.length * XP_PER_QUEST + XP_PER_SECTION, accent, level: prevLevel.current || undefined,
      })
    } else {
      window.setTimeout(() => scrollTo(cur.cardIndex + 1), 60)
    }
  }

  const scrollTo = (i: number) => {
    cardRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Mở HẾT nội dung cho mọi người (không khoá tuần tự) — 'ải' chỉ còn là huy hiệu tiến độ.
  const visibleCount = cards.length
  const completedCount = bypassLock ? cards.length : done.size

  return (
    <div>
      {/* Bản đồ lộ trình các ải */}
      <div className="mb-5 rounded-2xl border border-gray-100 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={16} style={{ color: accent }} />
          <span className="text-sm font-black text-gray-800">Lộ trình chinh phục</span>
          <button onClick={toggleFx} title="Bật/tắt hiệu ứng & âm thanh"
            className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border transition ${fx ? 'bg-violet-50 text-violet-600 border-violet-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
            <Sparkles size={12} /> {fx ? 'Hiệu ứng: Bật' : 'Hiệu ứng: Tắt'}
          </button>
          <span className="text-xs font-bold text-gray-400">
            {completedCount}/{cards.length} ải
          </span>
        </div>
        <div className="flex flex-col gap-0">
          {cards.map((c, i) => {
            const isDone = bypassLock || done.has(i)
            const st = stars[i] ?? 0
            return (
              <div key={c.id}>
                <button
                  onClick={() => scrollTo(i)}
                  className="w-full flex items-center gap-3 text-left hover:opacity-90"
                >
                  <span
                    className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center border-2 text-sm font-black"
                    style={{
                      background: isDone ? '#ecfdf5' : '#f8fafc',
                      borderColor: isDone ? '#10b981' : '#e2e8f0',
                      color: isDone ? '#059669' : '#94a3b8',
                    }}
                  >
                    {isDone ? <Check size={18} /> : i + 1}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-gray-800 truncate">
                      {c.title || `Hoạt động ${i + 1}`}
                    </span>
                    {isDone && gates[i] > 0 ? (
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3].map((n) => (
                          <Star key={n} size={13} className={n <= st ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
                        ))}
                      </span>
                    ) : (
                      <span className="block text-[11px] text-gray-400">
                        {isDone ? 'Đã hoàn thành' : 'Nhấn để xem'}
                      </span>
                    )}
                  </span>
                </button>
                {i < cards.length - 1 && (
                  <div className="ml-[18px] my-0.5 w-0.5 h-3" style={{ background: isDone ? '#a7f3d0' : '#e2e8f0' }} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Các ải được render lộ dần */}
      <div className="space-y-4">
        {cards.slice(0, visibleCount).map((card, i) => {
          const hasQ = gates[i] > 0
          return (
            <div
              key={card.id}
              ref={(el) => { cardRefs.current[i] = el }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6"
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="w-6 h-6 rounded-full text-white text-xs font-black flex items-center justify-center shrink-0"
                  style={{ background: done.has(i) || bypassLock ? '#10b981' : accent }}
                >
                  {i + 1}
                </span>
                <span className="text-xs font-black uppercase tracking-wide" style={{ color: accent }}>
                  Ải {i + 1}
                </span>
                {done.has(i) && !bypassLock && (
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                    <Check size={13} /> Đã qua
                  </span>
                )}
              </div>

              {(card as any).audioUrl && (
                <div className="mb-3 rounded-lg border border-orange-100 bg-orange-50/60 p-2 flex items-center gap-2">
                  <span className="text-base shrink-0" aria-hidden>🎧</span>
                  <span className="text-xs font-bold text-orange-800 shrink-0">Nghe giảng</span>
                  <audio controls preload="none" src={(card as any).audioUrl} className="flex-1 min-w-0 h-8" />
                </div>
              )}
              <LessonCardRenderer card={card} onComplete={hasQ ? () => complete(i) : undefined} />

              {/* Ải không có câu hỏi → nút xác nhận để ghi nhận hoàn thành (không bắt buộc) */}
              {!hasQ && !bypassLock && !done.has(i) && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => complete(i)}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-black text-white shadow-md hover:brightness-110"
                    style={{ background: accent }}
                  >
                    Đã hiểu, qua ải <Check size={16} />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Màn ăn mừng qua ải + hạ trùm (đóng băng giao diện) */}
      <QuestClearModal data={clear} fx={fx} onContinue={onQuestContinue} />
      <SectionClearModal data={sectionClear} fx={fx} onClose={() => setSectionClear(null)} />
    </div>
  )
}

export default QuestSection
